import { palette } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { useEffect, useRef, useState } from "react";
import { array, boolean, option } from "fp-ts";
import { constant, constNull, identity, pipe } from "fp-ts/function";
import { Option } from "fp-ts/Option";
import { sequenceS } from "fp-ts/Apply";
import {
  Dropdown,
  DropdownOption,
  Field,
  fieldIssues,
  FieldProps,
  LocalizedString,
  NonNegativeBrand,
  PositiveBrand,
  Stack,
  TextChildren,
  unsafeLocalizedString,
  unsafePositiveInteger,
  useComponentsWrapperContext,
} from "@dbn/omnichannel-design-system-g4p";
import { Month, MonthYear } from "../../../globalDomain";
import { months, useFormatMessage, useFormatMonth } from "../../../intl";
import {
  dropdownOptionToValue,
  selectedDropdownOption,
} from "../../../Common/selectDropdownOption";
import { Brand } from "io-ts";
import { ReadOnlyFieldToValue } from "../../../Common/ReadOnlyField/ReadOnlyFieldToValue";
import { useTooltipAccessibility } from "../../../Common/Tooltip/Tooltip";

type Props = FieldProps<Option<MonthYear>> & {
  id?: string;
  clearable: boolean;
  /**
   * Optional, longer description for the input field
   */
  description?: LocalizedString;
  descriptionAriaLabel?: LocalizedString;
  tooltipCloseIconAriaLabel?: LocalizedString;
  /**
   * Whether to automatically focus the input upon first render.
   * Only a single input element in a given view should have this prop set to true,
   * otherwise the last rendered one obtains the focus.
   */
  autoFocus?: boolean;
  assistiveText?: TextChildren;
  minYear: number;
  maxYear: number;
  ascending?: boolean;
  searchable: boolean;
  monthSelectionYearLimit: number;
  inverted?: boolean;
  checkMonthLimit: boolean;
  resetFlag?: any;
  defaultMonth?: number;
};

type MonthDropdownOption = DropdownOption<MonthYear["month"]>;
type YearDropdownOption = DropdownOption<MonthYear["year"]>;

type MonthYearDropdownState = {
  month: Option<MonthDropdownOption>;
  year: Option<YearDropdownOption>;
};

const getMonthYearOption = ({ month, year }: MonthYearDropdownState) =>
  pipe(
    {
      month: option.isNone(dropdownOptionToValue(month))
        ? option.some(11 as Month)
        : dropdownOptionToValue(month),
      year: dropdownOptionToValue(year),
    },
    sequenceS(option.option)
  );

export function MonthYearField(props: Props) {
  const formatMessage = useFormatMessage();
  const formatMonth = useFormatMonth();
  const { componentType } = useComponentsWrapperContext();

  const isRenderInverted = props.inverted ? props.inverted : false;

  const monthOptions = months.map(month => ({
    value: month,
    label: formatMonth(month),
  }));

  const yearOptions: YearDropdownOption[] = pipe(
    array.range(props.minYear, props.maxYear),
    props.ascending ? identity : array.reverse
  ).map(year => ({
    value: unsafePositiveInteger(year),
    label: unsafeLocalizedString(year),
  }));

  const [month, setMonth] = useState<Option<MonthDropdownOption>>(
    selectedDropdownOption(
      pipe(
        props.value,
        option.map(value => value.month)
      ),
      monthOptions
    )
  );
  const [year, setYear] = useState<Option<YearDropdownOption>>(
    selectedDropdownOption(
      pipe(
        props.value,
        option.map(value => value.year)
      ),
      yearOptions
    )
  );

  const monthDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);

  const onBlurDropdown = () => {
    setTimeout(() => {
      if (
        document.activeElement !==
          (monthDropdownRef.current &&
            (monthDropdownRef.current as any).select &&
            (monthDropdownRef.current as any).select.inputRef) &&
        document.activeElement !==
          (yearDropdownRef.current &&
            (yearDropdownRef.current as any).select &&
            (yearDropdownRef.current as any).select.inputRef)
      ) {
        props.onBlur();
      }
    }, 0);
  };

  const onChangeDropdown = (monthYear: MonthYearDropdownState) =>
    pipe(monthYear, getMonthYearOption, props.onChange);

  const renderMonthDropDown = (labelId: string) =>
    props.disabled ? (
      <ReadOnlyFieldToValue
        value={pipe(
          month,
          option.map(({ label }) => String(label)),
          option.getOrElse(constant(""))
        )}
        label={props.label}
      />
    ) : (
      <Dropdown
        {...props}
        ref={monthDropdownRef}
        width={!componentType || componentType === "g4p" ? "40%" : "50%"}
        name={`${props.name}_month`}
        options={monthOptions}
        id={props.id && `${props.id}_month`}
        key={`${labelId}_month`}
        value={month}
        onChange={value => {
          setMonth(value);
          onChangeDropdown({ month: value, year });
        }}
        onBlur={onBlurDropdown}
        isDisabled={props.disabled}
        aria-labelledby={labelId}
        placeholder={formatMessage("Month")}
        issueType={pipe(props.issues, option.map(fieldIssues.issuesType))}
        fieldLabel={props.label}
        cancelLabel={formatMessage("Cancel")}
        clearSelectionLabel={formatMessage("Dropdown.clearSelection")}
        filterInputPlaceholder={formatMessage(
          "Dropdown.filterInputPlaceholder"
        )}
        noResultsLabel={formatMessage("NoResults")}
      />
    );

  const renderMonth = (labelId: string) =>
    pipe(
      props.checkMonthLimit,
      boolean.fold(
        () => renderMonthDropDown(labelId),
        () =>
          pipe(
            year,
            option.fold(
              constNull,
              selectedYear =>
                selectedYear.value >
                  props.maxYear - props.monthSelectionYearLimit &&
                renderMonthDropDown(labelId)
            )
          )
      )
    );

  const defaultMonth = !!props.defaultMonth ? props.defaultMonth : 0;

  const onChangeYear = (
    year: Option<
      DropdownOption<number & Brand<NonNegativeBrand> & Brand<PositiveBrand>>
    >
  ) => {
    setYear(year);
    pipe(
      props.checkMonthLimit,
      boolean.fold(
        () => onChangeDropdown({ month, year }),
        () =>
          pipe(
            year,
            option.fold(constNull, selectedYear =>
              pipe(
                selectedYear.value >
                  props.maxYear - props.monthSelectionYearLimit,
                boolean.fold(
                  () =>
                    onChangeDropdown({
                      month: !!props.defaultMonth
                        ? option.some(monthOptions[defaultMonth])
                        : option.none,
                      year,
                    }),
                  () => onChangeDropdown({ month, year })
                )
              )
            )
          )
      )
    );
  };

  const [firstTime, setFirstTime] = useState(true);
  const tooltipAccessibility = useTooltipAccessibility(
    componentType,
    props.description,
    props.descriptionAriaLabel,
    props.tooltipCloseIconAriaLabel
  );

  useEffect(() => {
    pipe(
      firstTime,
      boolean.fold(
        () => onChangeYear(selectedDropdownOption(option.none, yearOptions)),
        () => setFirstTime(false)
      )
    );
  }, [props.resetFlag]);

  const renderYear = (labelId: string) =>
    props.disabled ? (
      <ReadOnlyFieldToValue
        value={pipe(
          year,
          option.map(({ label }) => String(label)),
          option.getOrElse(constant(""))
        )}
        label={props.label}
      />
    ) : (
      <Dropdown
        {...props}
        ref={yearDropdownRef}
        width={!componentType || componentType === "g4p" ? "40%" : "25%"}
        name={`${props.name}_year`}
        options={yearOptions}
        id={props.id && `${props.id}_year`}
        key={`${labelId}_year`}
        value={year}
        onChange={onChangeYear}
        onBlur={onBlurDropdown}
        isDisabled={props.disabled}
        autoFocus={false}
        aria-labelledby={labelId}
        placeholder={formatMessage("Year")}
        issueType={pipe(props.issues, option.map(fieldIssues.issuesType))}
        fieldLabel={props.label}
        cancelLabel={formatMessage("Cancel")}
        clearSelectionLabel={formatMessage("Dropdown.clearSelection")}
        filterInputPlaceholder={formatMessage(
          "Dropdown.filterInputPlaceholder"
        )}
        noResultsLabel={formatMessage("NoResults")}
      />
    );

  const labelColor =
    option.isSome(year) || option.isSome(month)
      ? palette.neutral700
      : palette.dark;

  return (
    <Field
      label={props.disabled ? ("" as LocalizedString) : props.label}
      labelColor={labelColor}
      description={props.description}
      {...tooltipAccessibility}
      issues={props.issues}
      assistiveText={props.assistiveText}
    >
      {labelId =>
        pipe(
          isRenderInverted,
          boolean.fold(
            () => (
              <Stack
                id={props.id}
                units={!componentType || componentType === "g4p" ? 8 : 4}
              >
                {renderMonth(labelId)}
                {renderYear(labelId)}
              </Stack>
            ),
            () => (
              <Stack
                id={props.id}
                units={!componentType || componentType === "g4p" ? 8 : 4}
              >
                {renderYear(labelId)}
                {renderMonth(labelId)}
              </Stack>
            )
          )
        )
      }
    </Field>
  );
}
